import { NavLink } from "react-router";

import { NAV, type NavItem } from "@/constants/routes";
import { useShadowProfile } from "@/features/auth/use-shadow-profile";
import { cx } from "@/utils/cx";

/**
 * Left-hand menu. Organization-scoped items on top, global items below.
 *
 * Items marked `requiresGlobalAccess` are hidden rather than disabled: the API
 * enforces the same flag on every route behind them, so there is nothing
 * for a viewer without the grant to reach. An external configurator sees
 * only the top group, and no "Global" heading at all.
 */
export const Sidebar = () => {
	const { globalAccess } = useShadowProfile();
	const visible = (items: readonly NavItem[]) =>
		items.filter((item) => !item.requiresGlobalAccess || globalAccess);

	const organization = visible(NAV.organization);
	const global = visible(NAV.global);

	return (
		<aside className="border-secondary flex w-56 shrink-0 flex-col gap-6 border-r px-3 py-4">
			<NavGroup items={organization} label="Organization" />
			{global.length > 0 && <NavGroup items={global} label="Global" />}
		</aside>
	);
};

interface NavGroupProps {
	items: readonly NavItem[];
	label: string;
}

const NavGroup = ({ items, label }: NavGroupProps) => (
	<nav aria-label={label} className="flex flex-col gap-1">
		<h2 className="text-tertiary px-3 pb-1 text-xs font-semibold uppercase tracking-wide">
			{label}
		</h2>
		{items.map((item) => (
			<NavLink
				className={({ isActive }) =>
					cx(
						"rounded-md px-3 py-2 text-sm font-medium",
						isActive
							? "bg-brand-50 text-brand-800"
							: "text-secondary hover:bg-secondary hover:text-primary",
					)
				}
				key={item.href}
				to={item.href}
			>
				{item.label}
			</NavLink>
		))}
	</nav>
);
